import { supabase } from "@/integrations/supabase/client";

export const checkUserRole = async (role: "admin" | "user"): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Enhanced role checking using the secure database function
    const { data: hasRole, error } = await supabase
      .rpc("has_role", { 
        _user_id: user.id, 
        _role: role 
      });

    if (error) {
      console.error("Error checking user role:", error);
      return false;
    }

    return !!hasRole;
  } catch (error) {
    console.error("Error checking user role:", error);
    return false;
  }
};

export const isAdmin = () => checkUserRole("admin");

// Enhanced admin verification with audit logging
export const verifyAdminAccess = async (action?: string): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: hasAccess, error } = await supabase
      .rpc("verify_admin_access", { 
        _user_id: user.id, 
        _action: action || "admin_access" 
      });

    if (error) {
      console.error("Error verifying admin access:", error);
      return false;
    }

    return !!hasAccess;
  } catch (error) {
    console.error("Error verifying admin access:", error);
    return false;
  }
};

// Update last login timestamp for user activity tracking
export const updateLastLogin = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Cast to any since last_login_at is a new column added via migration
    await supabase
      .from('profiles')
      .update({ last_login_at: new Date().toISOString() } as any)
      .eq('id', user.id);
  } catch (error) {
    console.error("Error updating last login:", error);
  }
};

// Session security helpers
export const checkSessionSecurity = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { isValid: false, reason: "No session" };

    // Check if session is expired
    const now = new Date().getTime() / 1000;
    if (session.expires_at && session.expires_at < now) {
      return { isValid: false, reason: "Session expired" };
    }

    // Get user information for additional checks
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { isValid: false, reason: "No user" };

    // Check user account age for suspicious activity
    if (user.created_at) {
      const userCreated = new Date(user.created_at).getTime() / 1000;
      const accountAge = now - userCreated;
      const minAccountAge = 5 * 60; // 5 minutes minimum
      
      if (accountAge < minAccountAge) {
        return { isValid: false, reason: "Account too new" };
      }
    }

    return { isValid: true };
  } catch (error) {
    console.error("Error checking session security:", error);
    return { isValid: false, reason: "Security check failed" };
  }
};

// Force session refresh for enhanced security
export const refreshSessionSecurity = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error("Failed to refresh session:", error);
      return false;
    }
    return !!data.session;
  } catch (error) {
    console.error("Error refreshing session:", error);
    return false;
  }
};